import React from "react";
import { Link } from "react-router-dom";
import {
  Plane, Ship, Truck, FileCheck2, Package, Warehouse,
  Building2, Boxes, ArrowUpRight, Sparkles, MapPin,
} from "lucide-react";
import { useT } from "@/lib/i18n";
import LogisticsAnimation from "@/components/LogisticsAnimation";

export default function ServicesGrid({ heading = true, animated = false }) {
  const { t } = useT();

  const SERVICES = [
    { icon: Plane, title: t("services.airTitle"), desc: t("services.airDesc"), tags: ["FRA Hub", "DG Ready"] },
    { icon: Ship, title: t("services.oceanTitle"), desc: t("services.oceanDesc"), tags: ["FCL · LCL", "DDP"] },
    { icon: Truck, title: t("services.roadTitle"), desc: t("services.roadDesc"), tags: ["FTL · LTL", "EU-Wide"] },
    { icon: FileCheck2, title: t("services.customsTitle"), desc: t("services.customsDesc"), tags: ["EU Customs", "AEO"] },
    { icon: Building2, title: t("services.ddpTitle"), desc: t("services.ddpDesc"), tags: ["DDP", "IOR"] },
    { icon: Package, title: t("services.fbaTitle"), desc: t("services.fbaDesc"), tags: ["FBA", "Prep + Label"] },
    { icon: Boxes, title: t("services.fairsTitle"), desc: t("services.fairsDesc"), tags: ["ATA Carnet", "On-Site"] },
    { icon: Warehouse, title: t("services.whTitle"), desc: t("services.whDesc"), tags: ["Bonded", "WMS"] },
    { icon: Package, title: t("services.ecomTitle"), desc: t("services.ecomDesc"), tags: ["DDP Parcels", "Last-Mile"] },
    { icon: MapPin, title: t("services.turkeyTitle"), desc: t("services.turkeyDesc"), tags: ["TR → EU", "LTL Network", "Bonded"], highlight: true, accent: "turkey" },
    { icon: Sparkles, title: t("services.wgTitle"), desc: t("services.wgDesc"), tags: ["Aerospace", "Automotive"], highlight: true },
  ];

  return (
    <section data-testid="services-section" className="relative py-24 sm:py-28 overflow-hidden">
      {animated && <LogisticsAnimation variant="all" className="opacity-90" />}
      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {heading && (
          <div className="max-w-2xl mb-14">
            <div className="zp-label">{t("services.eyebrow")}</div>
            <h2 className="font-display text-3xl sm:text-5xl tracking-tighter mt-3">
              {t("services.title.a")}{" "}
              <span className="text-[color:var(--zp-text-3)]">{t("services.title.b")}</span>
            </h2>
            <p className="text-[color:var(--zp-text-2)] mt-5 leading-relaxed">
              {t("services.subtitle")}
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {SERVICES.map((s, i) => (
            <article
              key={s.title + i}
              data-testid={`service-card-${i}`}
              className={`zp-card p-7 relative overflow-hidden group ${s.highlight ? "ring-1 ring-[color:var(--zp-cyan-2)]/40" : ""}`}
            >
              {s.highlight && (
                <div className="absolute top-4 right-4 text-[10px] uppercase tracking-[0.2em] font-bold text-[color:var(--zp-cyan-3)] bg-[color:var(--zp-cyan-soft)] px-2 py-1 rounded-full">
                  {s.accent === "turkey" ? "Turkey Lane" : "White-Glove"}
                </div>
              )}
              <div className="absolute -top-20 -right-16 w-56 h-56 rounded-full bg-[#5CE1E6]/20 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative">
                <div className="w-12 h-12 rounded-xl bg-[color:var(--zp-cyan-softer)] grid place-items-center text-[color:var(--zp-cyan-3)] group-hover:text-white group-hover:bg-[color:var(--zp-navy)] transition-colors">
                  <s.icon size={20} strokeWidth={1.7} />
                </div>
                <h3 className="font-display text-xl mt-5 tracking-tight">{s.title}</h3>
                <p className="text-[color:var(--zp-text-2)] text-sm mt-3 leading-relaxed">{s.desc}</p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {s.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-[11px] px-2.5 py-1 rounded-full border border-[color:var(--zp-border)] text-[color:var(--zp-text-2)] bg-[color:var(--zp-bg-2)] font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <Link
                  to="/services"
                  data-testid={`service-card-link-${i}`}
                  className="inline-flex items-center gap-1.5 text-sm mt-6 text-[color:var(--zp-cyan-3)] hover:text-[color:var(--zp-navy)] font-semibold"
                >
                  {t("common.exploreMore")} <ArrowUpRight size={14} />
                </Link>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
